import { type Lens } from '@dhmk/zustand-lens';
import { sendEvent } from '@onyx/observability-v2/client';
import { handlePromise } from '@onyx/utils/promises';

import {
  cancelChatBotRequest,
  getChatBotData,
} from '../app/chatbot/chatbot-actions';
import type { Message } from '../app/chatbot/types';
import { EndReason } from '../app/events/event-types';
import {
  sendChatEndedEvent,
  sendChatOutcomeEvent,
  sendChatStartedEvent,
  sendChatSubmittedEvent,
  sendChatViewSourceEvent,
} from '../app/events/events-action';
import { resolveChatbotError } from '../utils/help-hub-error-codes';
import type { HelpHubPlatform } from '../utils/help-hub-observability';

export type Chat = {
  id: string;
  messages: Message[];
  createdAt: Date;
  updatedAt: Date;
  sessionId: string;
};

export type PendingOutcome = {
  sessionId: string;
  pid: string;
  category: string;
};

type State = {
  chats: Record<string, Chat>;
  isLoading: boolean;
  error?: string;
  lastFailedMessage?: { chatId: string; content: string };
  pendingOutcome?: PendingOutcome;
};

export const viewSourceMessage =
  'Can you share me the reference from M&S way with section details?';

type Actions = {
  sendMessage: (
    chatId: string,
    content: string,
    platform?: HelpHubPlatform
  ) => Promise<void>;
  addMessage: (chatId: string, message: Message) => void;
  createChat: (chatId?: string) => string;
  clearChat: (chatId: string) => void;
  retryLastMessage: (platform?: HelpHubPlatform) => Promise<void>;
  clearError: () => void;
  cancelRequest: (chatId?: string) => void;
  setPendingOutcome: (sessionId: string, activityId: string) => void;
  clearPendingOutcome: (ticketId?: string) => void;
};

export type ChatbotSlice = State & Actions;

type Slice = { chatbot: ChatbotSlice };

const DEFAULT_CHAT_ID = 'new';

const generateSessionId = () => crypto.randomUUID();

const createDefaultState = (): State => ({
  chats: {},
  isLoading: false,
  error: undefined,
  lastFailedMessage: undefined,
});

export class ChatbotStore {
  createSlice = (initialState: Partial<State> = {}) => {
    const state: Lens<ChatbotSlice> = (set, get) => ({
      ...createDefaultState(),
      ...initialState,

      sendMessage: async (
        chatId: string,
        content: string,
        platform?: HelpHubPlatform
      ) => {
        const currentState = get();
        // eslint-disable-next-line functional/no-let
        let chat = currentState.chats[chatId];

        // Create chat if it doesn't exist
        if (!chat) {
          chat = {
            id: chatId,
            messages: [],
            createdAt: new Date(),
            updatedAt: new Date(),
            sessionId: generateSessionId(),
          };
          set((prev) => ({
            ...prev,
            chats: {
              ...prev.chats,
              [chatId]: chat,
            },
          }));

          // Fire ChatStartedEvent for new chat
          void handlePromise(
            sendChatStartedEvent(chat.sessionId, {
              category: 'home',
              intent: content,
            })
          );
        }

        const userMessage: Message = {
          role: 'user',
          content,
          timestamp: new Date().toISOString(),
        };
        const updatedMessages = [...chat.messages, userMessage];

        // Add user message and set loading
        set((prev) => ({
          ...prev,
          isLoading: true,
          error: undefined,
          chats: {
            ...prev.chats,
            [chatId]: {
              ...prev.chats[chatId],
              messages: updatedMessages,
              updatedAt: new Date(),
            },
          },
        }));

        // eslint-disable-next-line functional/no-try-statement
        try {
          const result = await getChatBotData({
            messages: updatedMessages,
            chatId,
            sessionId: chat.sessionId,
            platform,
          });

          // Handle abort
          if (!result.isSuccess && result.error === 'AbortError') {
            return;
          }

          if (!result.isSuccess) {
            set((prev) => ({
              ...prev,
              isLoading: false,
              error: result.error,
              lastFailedMessage: { chatId, content },
            }));

            return;
          }

          const response = result.data;

          if (response) {
            const { assistantMessage } = response;
            const responseWithTimestamp: Message = {
              ...assistantMessage,
              timestamp: new Date().toISOString(),
            };
            set((prev) => ({
              ...prev,
              isLoading: false,
              chats: {
                ...prev.chats,
                [chatId]: {
                  ...prev.chats[chatId],
                  id: chatId,
                  messages: [
                    ...prev.chats[chatId].messages,
                    responseWithTimestamp,
                  ],
                  updatedAt: new Date(),
                },
              },
            }));

            // Fire ChatSubmittedEvent or ChatViewSourceEvent
            const currentChat = get().chats[chatId];

            if (!assistantMessage.id) {
              return;
            }

            if (content === viewSourceMessage) {
              void handlePromise(
                sendChatViewSourceEvent(currentChat.sessionId, {
                  category: 'home',
                  pid: assistantMessage.id.split(':')[0],
                })
              );
            } else {
              void handlePromise(
                sendChatSubmittedEvent(currentChat.sessionId, {
                  category: 'home',
                  pid: assistantMessage.id.split(':')[0],
                })
              );
            }

            set((prev) => ({
              ...prev,
              pendingOutcome: undefined,
            }));
          } else {
            set((prev) => ({
              ...prev,
              isLoading: false,
            }));
          }
        } catch {
          const { message: errorMessage } = resolveChatbotError({
            reason: 'unknown',
          });
          set((prev) => ({
            ...prev,
            isLoading: false,
            error: errorMessage,
            lastFailedMessage: { chatId, content },
          }));
        }
      },

      addMessage: (chatId: string, message: Message) => {
        set((prev) => {
          const chat = prev.chats[chatId];
          if (!chat) {
            return {
              ...prev,
              chats: {
                ...prev.chats,
                [chatId]: {
                  id: chatId,
                  messages: [message],
                  createdAt: new Date(),
                  updatedAt: new Date(),
                  sessionId: generateSessionId(),
                },
              },
            };
          }
          return {
            ...prev,
            chats: {
              ...prev.chats,
              [chatId]: {
                ...chat,
                messages: [...chat.messages, message],
                updatedAt: new Date(),
              },
            },
          };
        });
      },

      createChat: (chatId?: string) => {
        const id = chatId || DEFAULT_CHAT_ID;
        set((prev) => ({
          ...prev,
          chats: {
            ...prev.chats,
            [id]: {
              id,
              messages: [],
              createdAt: new Date(),
              updatedAt: new Date(),
              sessionId: generateSessionId(),
            },
          },
        }));
        return id;
      },

      clearChat: (chatId: string) => {
        const previousSessionId = get().chats[chatId]?.sessionId;
        const chat = get().chats[chatId];

        set((prev) => {
          const chatToClean = prev.chats[chatId];
          if (!chatToClean) {
            return prev;
          }
          return {
            ...prev,
            chats: {
              ...prev.chats,
              [chatId]: {
                ...chatToClean,
                messages: [],
                updatedAt: new Date(),
                sessionId: generateSessionId(),
              },
            },
          };
        });

        // Fire ChatEndedEvent with EndReason
        if (chat && previousSessionId) {
          void handlePromise(
            sendChatEndedEvent(previousSessionId, {
              end: EndReason.UserClosed,
            })
          );
        }
      },

      retryLastMessage: async (platform?: HelpHubPlatform) => {
        const currentState = get();
        const { lastFailedMessage } = currentState;
        if (!lastFailedMessage) {
          return;
        }

        const { chatId, content } = lastFailedMessage;

        sendEvent('help-hub.chatbot.message.retried', {
          'chatbot.chat_id': chatId,
          'chatbot.session_id': currentState.chats[chatId]?.sessionId,
        });

        set((prev) => ({
          ...prev,
          error: undefined,
          lastFailedMessage: undefined,
        }));

        const chat = currentState.chats[chatId];
        if (chat && chat.messages.length > 0) {
          const lastMessage = chat.messages.at(-1);
          if (
            lastMessage?.role === 'user' &&
            lastMessage?.content === content
          ) {
            set((prev) => ({
              ...prev,
              chats: {
                ...prev.chats,
                [chatId]: {
                  ...prev.chats[chatId],
                  messages: prev.chats[chatId].messages.slice(0, -1),
                },
              },
            }));
          }
        }

        await get().sendMessage(chatId, content, platform);
      },

      clearError: () => {
        set((prev) => ({
          ...prev,
          error: undefined,
          lastFailedMessage: undefined,
        }));
      },

      cancelRequest: (chatId?: string) => {
        sendEvent('help-hub.chatbot.request.cancelled', {
          'chatbot.chat_id': chatId,
          'chatbot.session_id': chatId
            ? get().chats[chatId]?.sessionId
            : undefined,
        });
        void cancelChatBotRequest();
        set((prev) => ({
          ...prev,
          isLoading: false,
        }));
      },

      setPendingOutcome: (sessionId: string, activityId: string) => {
        const pid = activityId.split(':')[0];

        if (!pid) {
          return;
        }

        set((prev) => ({
          ...prev,
          pendingOutcome: { sessionId, pid, category: 'home' },
        }));
      },

      clearPendingOutcome: (ticketId) => {
        const pendingOutcome = get().pendingOutcome;
        if (pendingOutcome && ticketId) {
          void handlePromise(
            sendChatOutcomeEvent(pendingOutcome.sessionId, {
              category: pendingOutcome.category,
              pid: pendingOutcome.pid,
              ticket_raised: true,
              ticket_id: ticketId,
            })
          );
        }
        set((prev) => ({ ...prev, pendingOutcome: undefined }));
      },
    });

    return state;
  };

  partialize = ({ chatbot }: Slice): Partial<ChatbotSlice> => ({
    chats: chatbot.chats,
    pendingOutcome: chatbot.pendingOutcome,
  });
}

export const selectChatbot = ({ chatbot }: Slice) => ({
  chats: chatbot.chats,
  isLoading: chatbot.isLoading,
  error: chatbot.error,
  sendMessage: chatbot.sendMessage,
  addMessage: chatbot.addMessage,
  createChat: chatbot.createChat,
  clearChat: chatbot.clearChat,
  retryLastMessage: chatbot.retryLastMessage,
  clearError: chatbot.clearError,
  cancelRequest: chatbot.cancelRequest,
});

export const selectChat = (chatId: string) => {
  return ({
    chatbot,
  }: Slice): {
    chat: Chat | undefined;
    messages: Message[];
    isLoading: boolean;
    error: string | undefined;
    sendMessage: (content: string, platform?: HelpHubPlatform) => Promise<void>;
    clearChat: () => void;
    retryLastMessage: (platform?: HelpHubPlatform) => Promise<void>;
    clearError: () => void;
    cancelRequest: () => void;
  } => {
    const chat = chatbot.chats[chatId];
    return {
      chat,
      messages: chat?.messages || [],
      isLoading: chatbot.isLoading,
      error: chatbot.error,
      sendMessage: (content: string, platform?: HelpHubPlatform) =>
        chatbot.sendMessage(chatId, content, platform),
      clearChat: () => chatbot.clearChat(chatId),
      retryLastMessage: chatbot.retryLastMessage,
      clearError: chatbot.clearError,
      cancelRequest: () => chatbot.cancelRequest(chatId),
    };
  };
};

export const selectPendingOutcome = ({ chatbot }: Slice) => ({
  pendingOutcome: chatbot.pendingOutcome,
  clearPendingOutcome: chatbot.clearPendingOutcome,
});
